// src/ui/bubbles/BubbleLayer.tsx — Phase 6 Plan 04 top-level bubble layer.
//
// Subscribes to renderBus speech events and renders one absolutely-positioned
// <Bubble /> per visible bubble. Cap-3 with tier preempt (D-02). Per-frame
// world→screen position update via getGameSceneRef().getSpriteScreenPos
// (Pitfall 11 — single shared scratch Vector2). Player-bubble fires
// applyCameraBias on mount + releaseCameraBias on auto-expire (D-03). NPC
// bubbles use no camera shift; tail auto-flip on safe-area collision is
// handled at the Bubble level via the tailDirection prop.
//
// Subscriptions (≥ 7 renderBus events resolve text via the cache OR fixed
// strings — see tierForEvent + textForEvent helpers):
//   - 'character-spoke'         → flavour-tier; getQuote(characterId, eventTag, lineIndex)
//   - 'adult-commented'         → commentary-tier; getCommentary(eventTag)[lineIndex]
//   - 'foxguard-called'         → critical-tier (foxguard styled); fixed 'Foxguarding!'
//   - 'fear-threshold-crossed'  → critical-tier; gated to e0+up
//   - 'shout'                   → shout-tier; fixed '!'
//   - 'catch'                   → critical-tier; fixed 'Caught!' (hunter speaker)
//   - 'hider-released'          → critical-tier; fixed 'Liberdade!' (liberator speaker)
//   - 'dog-barked'              → flavour-tier; fixed 'Woof!'
//
// XSS guard (T-6-04-01): every bubble text path flows through React {text}
// (Bubble.tsx renders the literal text node) — the raw-HTML escape hatch is
// intentionally never used. Test 10 in BubbleLayer.test.tsx pins this.

import {
  createRef,
  useEffect,
  useRef,
  useState,
  type JSX,
  type RefObject,
} from 'react';

import { isDebugEnabled } from '../../debug-flag';
import { renderBus } from '../../render/bus';
import type { EntityStatus } from '../../shared/types/world';
import {
  P6_BUBBLE_CAP,
  P6_BUBBLE_LIFETIME_MIN_MS,
  P6_BUBBLE_LIFETIME_PADDING_MS,
  P6_BUBBLE_PER_ENTITY_REPLACE,
  P6_WAFFLE_SYLLABLE_INTERVAL_MS,
  P6_BUBBLE_SAFE_AREA_FLIP_PX,
} from '../../render/audio/constants';
import { getGameSceneRef } from '../../render/PhaserGame';
import { getQuote } from '../../sim/content/cache';
import { getCommentary } from '../../sim/npcs/adults/commentary';

import { Bubble, type BubbleTailDirection } from './Bubble';
import {
  TIER_RANK,
  tryAdd,
  type BubbleEntry,
  type BubbleTier,
} from './bubble-priority';
import { applyCameraBias, releaseCameraBias } from './camera-bias';

const PLAYER_ENTITY_ID = 'e0';

/**
 * Plan 06-10 (UAT Gap 1/6 closure) — module-scope phase ref.
 *
 * Defence-in-depth phase guard: every renderBus admit() handler short-circuits
 * when the most recent sim-tick payload's `round.phase` is not 'active'.
 * The primary defence is the sim-tier gates on Stages 17/21/22/23 + the
 * round-started/round-ended speech emissions in src/sim/tick.ts; this UI-side
 * ref catches any future regression in the sim-tier gates AND silences any
 * boundary-tick race in the team-switch handoff.
 *
 * Initial value 'active' is intentional: Storybook / pre-sim-tick mounts must
 * not silently swallow bubbles. The first sim-tick (~33 ms after sim boot)
 * overwrites this with the real phase.
 *
 * Source: src/render/bus.ts L82-107 — sim-tick payload carries `round: RoundState`
 * (NOT a top-level `roundPhase`), so the phase field lives at `payload.round.phase`.
 *
 * Plan-coordination shape (from 06-10 INTERLEAVING NOTES):
 *
 *   function admit(entry: BubbleEntryWithMeta): void {
 *     // 06-10 PHASE GATE (this plan):
 *     if (currentRoundPhase !== 'active') return;
 *     // 06-12 will add an entityStatuses guard here for fear-threshold-crossed
 *     // 06-14 will add a per-entity replace filter INSIDE the setBubbles cb
 *     // ... existing tryAdd / setBubbles logic UNCHANGED ...
 *   }
 */
let currentRoundPhase: 'headstart' | 'active' | 'ended' = 'active';

/**
 * Plan 06-12 (UAT Gap 4 closure) — module-scope per-entity status snapshot.
 *
 * Updated from each renderBus 'sim-tick' payload's optional entityStatuses
 * field (extends — does NOT duplicate — the existing 06-10 sim-tick handler
 * that updates currentRoundPhase). Read by the fear-threshold-crossed admit
 * handler so a hidden (or caught) speaker's `!` / `...!` bubble is dropped
 * silently. Defence layer 2 of three (sim-tier defensive guard at
 * src/sim/tick.ts maybeEmitCharacterSpoke is layer 1; the hiding-entered
 * cleanup below is layer 3).
 *
 * Source: src/render/bus.ts L82-118 — sim-tick payload now carries
 * `entityStatuses?: Record<string, EntityStatus>`. Optional `?` for
 * backward compat with existing test fixtures that construct partial
 * payloads. Initial value `{}` is permissive (admit() proceeds normally
 * for any speaker until the first sim-tick arrives).
 */
let entityStatuses: Record<string, EntityStatus> = {};

/** Module-augmented bubble entry with the foxguard styling flag — only
 *  read at render time so it sits next to the cap/preempt tier without
 *  polluting the pure tryAdd contract. */
interface BubbleEntryWithMeta extends BubbleEntry {
  foxguard?: boolean;
}

function durationFor(text: string): number {
  // DIAL-07: text duration = max(waffle + 500ms, 1500ms).
  // waffle ≈ syllableCount × P6_WAFFLE_SYLLABLE_INTERVAL_MS;
  // syllableCount ≈ ceil(text.length / 4) — same formula 06-03 audio
  // manager will use when wiring waffle line duration in 06-05.
  const syllableCount = Math.max(1, Math.ceil(text.length / 4));
  const waffleMs = syllableCount * P6_WAFFLE_SYLLABLE_INTERVAL_MS;
  return Math.max(
    waffleMs + P6_BUBBLE_LIFETIME_PADDING_MS,
    P6_BUBBLE_LIFETIME_MIN_MS,
  );
}

function decideTailDirection(node: HTMLDivElement): BubbleTailDirection {
  // P6-Pitfall H: 8px hysteresis at the safe-area top edge. If the bubble
  // top would render above the safe-area inset (or within FLIP_PX of it),
  // flip the tail UP so the bubble appears below the sprite instead.
  // We approximate the safe-area top via getComputedStyle on documentElement
  // env(safe-area-inset-top); fall back to 0 in jsdom or browsers that
  // don't expose it.
  const root =
    typeof document !== 'undefined' ? document.documentElement : null;
  const safeTopRaw = root
    ? getComputedStyle(root).getPropertyValue('--safe-area-inset-top')
    : '';
  const safeTop = safeTopRaw ? parseFloat(safeTopRaw) || 0 : 0;
  const rect = node.getBoundingClientRect();
  return rect.top - safeTop < P6_BUBBLE_SAFE_AREA_FLIP_PX ? 'up' : 'down';
}

export function BubbleLayer(): JSX.Element {
  const [bubbles, setBubbles] = useState<BubbleEntryWithMeta[]>([]);
  const [preemptedIds, setPreemptedIds] = useState<Set<string>>(() => new Set());
  const [tailDirs, setTailDirs] = useState<Record<string, BubbleTailDirection>>({});
  const refs = useRef<Map<string, RefObject<HTMLDivElement | null>>>(new Map());
  // Capture once at mount — re-subscribing on a per-bubble basis would be
  // wasteful, and the user does not toggle prefers-reduced-motion mid-session.
  const reducedMotion = useRef<boolean>(
    typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  ).current;

  // Subscribe ONCE at mount to the 8 bubble-trigger renderBus events PLUS
  // the 'sim-tick' phase-ref subscription (Plan 06-10 — defence-in-depth
  // for the sim-tier phase gates on Stages 17/21/22/23 + round-started/
  // round-ended speech).
  // Each handler resolves text + tier and feeds an entry through the
  // pure tryAdd preempt function (D-02).
  useEffect(() => {
    // Plan 06-14 sequencing (UAT 2026-06-11 decision): when a lower-priority
    // bubble is dropped because the same speaker already owns a higher-priority
    // one (e.g. a hunter's flavour quote arriving behind its own 'Caught!'),
    // we DEFER it here instead of discarding it. When the higher-priority
    // bubble auto-expires, the deferred follow-up is replayed — so the catch /
    // free-all moment reads as a SEQUENCE ('Caught!' then the character's
    // flavour line) rather than one bubble eating the other. At most one
    // pending follow-up per entity (latest wins).
    const pendingFollowups = new Map<string, BubbleEntryWithMeta>();

    function admit(entry: BubbleEntryWithMeta): void {
      // Plan 06-10 PHASE GATE (UAT Gap 1/6 — defence-in-depth):
      // Short-circuit every admit path when the most recent sim-tick
      // payload's round.phase is not 'active'. Future plans (06-12, 06-14)
      // will add additional admit-time checks; the canonical post-06-10
      // shape is documented at the module-scope currentRoundPhase declaration.
      if (currentRoundPhase !== 'active') return;

      // Plan 06-14 PER-ENTITY REPLACE-ON-ADMIT (UAT Gap 8 closure):
      // Each entity displays AT MOST ONE bubble at a time. When a new bubble
      // arrives for an entityId that already owns a bubble on screen, the
      // older one is preempted (evicted from state) BEFORE tryAdd considers
      // the global cap. The user-observed bug — round-start quote and
      // 'Liberdade!' free-all critical bubble for the SAME speaker (e0 in
      // single-player) overlapping visually — is eliminated. The global tier
      // preempt logic (D-02) continues to apply to the FILTERED list, so
      // cross-entity admissions still respect cap-3 + tier rules (Test 38).
      //
      // Cleanup side effects (refs / preemptedIds / tailDirs) for replaced
      // ids run ONCE outside the setBubbles updater, mirroring the auto-
      // expire setTimeout block below and the canonical 06-12 hiding-entered
      // cleanup. The replacedIdsRef ref-tracked array pattern is safe against
      // React 18 strict-mode double-invocation of setBubbles (Mn11 fix —
      // updater RESETS the ref on each invoke, so the post-commit side-effect
      // loop sees the correct final list).
      const replacedIdsRef = { current: [] as string[] };
      let admitted = false;
      setBubbles((prev) => {
        replacedIdsRef.current = []; // reset on each invoke (strict-mode safety)
        // Plan 06-14 fix (UAT 2026-06-11): the per-entity replace must be
        // TIER-AWARE. The original unconditional same-entity eviction let a
        // lower-priority bubble (e.g. a hunter's flavour quote) evict that same
        // speaker's higher-priority CRITICAL bubble ('Caught!' / 'Liberdade!'),
        // because this filter runs BEFORE tryAdd's tier preempt. Guard: if the
        // entity already owns a STRICTLY HIGHER-priority bubble, DEFER the
        // incoming entry (sequencing — see pendingFollowups) instead of
        // showing it now. Equal-or-higher incoming tiers still replace
        // (critical-over-flavour and flavour-over-flavour both work),
        // preserving the Gap 8 overlap fix.
        //
        // The pendingFollowups.set runs INSIDE the updater (idempotent — same
        // key/value on a strict-mode double-invoke) because the updater is the
        // only place with access to `prev`, and reading a ref set here from the
        // post-commit code is NOT reliable (React may run the updater after the
        // synchronous tail). Mirrors the replacedIdsRef-push-inside-filter idiom.
        if (P6_BUBBLE_PER_ENTITY_REPLACE) {
          const incumbent = prev.find((b) => b.entityId === entry.entityId);
          if (incumbent && TIER_RANK[incumbent.tier] < TIER_RANK[entry.tier]) {
            pendingFollowups.set(entry.entityId, entry); // replay after incumbent expires
            return prev;
          }
        }
        const filtered = P6_BUBBLE_PER_ENTITY_REPLACE
          ? prev.filter((b) => {
              if (b.entityId === entry.entityId) {
                replacedIdsRef.current.push(b.id);
                return false;
              }
              return true;
            })
          : prev;
        const next = tryAdd(filtered, entry, P6_BUBBLE_CAP) as BubbleEntryWithMeta[];
        // tryAdd returns the same reference on a silent drop — reference
        // identity is the cleanest "was this admitted?" signal. Compare
        // against `filtered` (post-replace) so a same-entity replace where
        // tryAdd appends correctly counts as admitted even when the prior
        // entry was evicted.
        if (next === filtered && replacedIdsRef.current.length === 0) {
          return prev;
        }
        admitted = true;
        // If we evicted somebody at the lowest tier, mark them preempted
        // so their CSS exit class triggers a fade-out before unmount.
        // Do NOT attempt to clean up the preempted entries here — the React
        // setState re-render on this same tick already replaced them.
        return next;
      });

      // Plan 06-14: post-commit cleanup for replaced ids (refs + preemptedIds
      // + tailDirs). Mirrors the canonical hiding-entered cleanup pattern
      // landed in Plan 06-12 — same shape, same strict-mode-safety guarantees.
      for (const rid of replacedIdsRef.current) {
        refs.current.delete(rid);
      }
      if (replacedIdsRef.current.length > 0) {
        setPreemptedIds((s) => {
          let next = s;
          for (const rid of replacedIdsRef.current) {
            if (next.has(rid)) {
              if (next === s) next = new Set(s);
              next.delete(rid);
            }
          }
          return next;
        });
        setTailDirs((m) => {
          let changed = false;
          const next: typeof m = { ...m };
          for (const rid of replacedIdsRef.current) {
            if (rid in next) {
              delete next[rid];
              changed = true;
            }
          }
          return changed ? next : m;
        });
      }

      // A deferred entry (stashed in pendingFollowups inside the updater) is
      // not admitted now — it replays from the incumbent's auto-expire flush.
      if (!admitted) return;

      // P6 Plan 06-07 (debug telemetry): emit 'bubble-shown' so the
      // DevOverlay can render the bubble counter row. Ships in prod but
      // gated at runtime by isDebugEnabled() (?debug=1 or localStorage).
      if (isDebugEnabled()) {
        renderBus.emit('bubble-shown', {
          entityId: entry.entityId,
          tier: entry.tier,
          tick: 0,
        });
      }

      if (entry.entityId === PLAYER_ENTITY_ID) {
        applyCameraBias();
      }

      // Auto-expire: filter the entry by id; if it was the player, release.
      window.setTimeout(() => {
        setBubbles((curr) => {
          if (!curr.some((b) => b.id === entry.id)) return curr;
          return curr.filter((b) => b.id !== entry.id);
        });
        // Use the entry's own entityId — the bubble may have already been
        // preempted out, but if a player bubble was admitted at all, the
        // matched apply needs a release. The idempotent guard inside
        // releaseCameraBias() makes a redundant call a no-op.
        if (entry.entityId === PLAYER_ENTITY_ID) {
          releaseCameraBias();
        }
        // Clean up ref + preempted entry on expire.
        refs.current.delete(entry.id);
        setPreemptedIds((s) => {
          if (!s.has(entry.id)) return s;
          const n = new Set(s);
          n.delete(entry.id);
          return n;
        });
        setTailDirs((m) => {
          if (!(entry.id in m)) return m;
          const n = { ...m };
          delete n[entry.id];
          return n;
        });
        // Sequencing flush: if this entity has a deferred follow-up waiting
        // (e.g. the hunter's flavour quote queued behind its 'Caught!'), replay
        // it now that the incumbent has cleared. Refresh mountedAt so the
        // typewriter + auto-expire start fresh. If the entity meanwhile took on
        // another higher-priority bubble, admit() will simply re-defer it.
        const pending = pendingFollowups.get(entry.entityId);
        if (pending) {
          pendingFollowups.delete(entry.entityId);
          admit({ ...pending, mountedAt: performance.now() });
        }
      }, entry.durationMs);
    }

    // ----- 'sim-tick' (Plan 06-10 — phase ref update; Plan 06-12 — extends
    // the SAME callback to also update entityStatuses) -----
    // Per src/render/bus.ts L82-107 the sim-tick payload carries
    // `round: RoundState` (NOT a top-level `roundPhase`); the phase field
    // lives at `payload.round.phase`. Plan 06-12 (UAT Gap 4) adds the
    // optional `entityStatuses?: Record<string, EntityStatus>` field on the
    // same payload — pulled in here so the fear-threshold-crossed admit
    // handler can short-circuit for hidden speakers. This handler does NOT
    // call admit() — it only updates module-scope refs that admit() reads.
    const offSimTick = renderBus.on('sim-tick', (payload) => {
      currentRoundPhase = payload.round.phase;
      if (payload.entityStatuses) entityStatuses = payload.entityStatuses;
    });

    // ----- 'character-spoke' (flavour-tier) -----
    const offSpoke = renderBus.on('character-spoke', (e) => {
      let text: string;
      try {
        text = getQuote(e.characterId, e.eventTag, e.lineIndex) ?? '...';
      } catch {
        // Cache not hydrated (defensive — App.tsx gates this layer behind
        // content.ready, but a unit test could mount the layer pre-hydrate).
        text = '...';
      }
      admit({
        id: `spoke:${e.entityId}:${e.tick}`,
        tier: 'flavour',
        mountedAt: performance.now(),
        entityId: e.entityId,
        characterId: e.characterId,
        text,
        durationMs: durationFor(text),
      });
    });

    // ----- 'adult-commented' (commentary-tier) -----
    const offAdult = renderBus.on('adult-commented', (e) => {
      let text: string;
      try {
        const lines = getCommentary(e.eventTag);
        text = lines[e.lineIndex] ?? '...';
      } catch {
        text = '...';
      }
      admit({
        id: `adult:${e.adultId}:${e.tick}`,
        tier: 'commentary',
        mountedAt: performance.now(),
        entityId: e.adultId,
        text,
        durationMs: durationFor(text),
      });
    });

    // ----- 'foxguard-called' (critical-tier, foxguard styled) -----
    const offFox = renderBus.on('foxguard-called', (e) => {
      const text = 'Foxguarding!';
      admit({
        id: `fox:${e.shouterId}:${e.tick}`,
        tier: 'critical',
        mountedAt: performance.now(),
        entityId: e.shouterId,
        text,
        durationMs: durationFor(text),
        foxguard: true,
      });
    });

    // ----- 'fear-threshold-crossed' (critical-tier; gated e0 + up) -----
    const offFear = renderBus.on('fear-threshold-crossed', (e) => {
      if (e.entityId !== PLAYER_ENTITY_ID || e.direction !== 'up') return;
      // P6 D-11 parity (Plan 06-12, UAT Gap 4): hidden / caught speakers are
      // silent. accumulators.ts keeps accumulating dark+isolation fear for
      // hidden hiders (only trigger-fear is gated to status='free' at
      // accumulators.ts:115); without this guard a hidden e0 in a dark spot
      // crosses 'afraid' and fires the `!` bubble (the user-observed UAT
      // Test 2 bug). Status snapshot maintained by the sim-tick handler above.
      const status = entityStatuses[e.entityId];
      if (status === 'hiding' || status === 'caught') return;
      const text = e.threshold === 'terrified' ? '...!' : '!';
      admit({
        id: `fear:${e.entityId}:${e.tick}`,
        tier: 'critical',
        mountedAt: performance.now(),
        entityId: e.entityId,
        text,
        durationMs: durationFor(text),
      });
    });

    // ----- 'shout' (shout-tier) -----
    const offShout = renderBus.on('shout', (e) => {
      const text = '!';
      admit({
        id: `shout:${e.sourceId}:${e.tick}`,
        tier: 'shout',
        mountedAt: performance.now(),
        entityId: e.sourceId,
        text,
        durationMs: durationFor(text),
      });
    });

    // ----- 'catch' (critical-tier; hunter speaker) -----
    const offCatch = renderBus.on('catch', (e) => {
      const text = 'Caught!';
      admit({
        id: `catch:${e.hunterId}:${e.tick}`,
        tier: 'critical',
        mountedAt: performance.now(),
        entityId: e.hunterId,
        text,
        durationMs: durationFor(text),
      });
    });

    // ----- 'hider-released' (critical-tier; liberator speaker) -----
    const offFreeAll = renderBus.on('hider-released', (e) => {
      const text = 'Liberdade!';
      admit({
        id: `free:${e.liberatorId}:${e.tick}`,
        tier: 'critical',
        mountedAt: performance.now(),
        entityId: e.liberatorId,
        text,
        durationMs: durationFor(text),
      });
    });

    // ----- 'dog-barked' (flavour-tier) -----
    const offDog = renderBus.on('dog-barked', (e) => {
      const text = 'Woof!';
      admit({
        id: `dog:${e.dogId}:${e.tick}`,
        tier: 'flavour',
        mountedAt: performance.now(),
        entityId: e.dogId,
        text,
        durationMs: durationFor(text),
      });
    });

    // ----- 'hiding-entered' (Plan 06-12 — bubble cleanup; B4 + Mn11 fix) -----
    // Defence layer 3 of three (sim-tier defensive guard at
    // src/sim/tick.ts maybeEmitCharacterSpoke is layer 1; the fear handler
    // status guard above is layer 2). When the local player (entityId === 'e0'
    // in practice; this implementation is generic so any entity's bubbles
    // dismiss when they hide) enters 'hiding', any in-flight bubble owned by
    // that entity is dismissed within ≤1 React flush — kills the lingering
    // round-start quote bubble that the user saw dangling above an empty
    // hide spot in UAT Test 2 (the SECONDARY bug).
    //
    // The cleanup snippet below is the EXACT canonical form from
    // 06-12-PLAN.md INTERLEAVING NOTES. The droppedIdsRef ref-tracked array
    // is safe against React 18 strict-mode double-invocation of the
    // setBubbles updater (Mn11 fix from checker). Side-effect cleanup of
    // refs / preemptedIds / tailDirs runs ONCE after React commits, mirroring
    // the auto-expire setTimeout block in admit() lines ~144-169.
    const offHidingEntered = renderBus.on('hiding-entered', (e) => {
      // Collect ids to drop in a ref-tracked array (safe against React 18
      // strict-mode double-invocation of the setBubbles updater — Mn11 fix).
      const droppedIdsRef = { current: [] as string[] };
      setBubbles((prev) => {
        droppedIdsRef.current = []; // reset on each invoke (strict mode safety)
        const next = prev.filter((b) => {
          if (b.entityId === e.entityId) {
            droppedIdsRef.current.push(b.id);
            return false;
          }
          return true;
        });
        return next === prev || droppedIdsRef.current.length === 0 ? prev : next;
      });
      // Side-effect cleanup runs ONCE after React commits.
      // refs.current is a Map of bubbleId -> DOM node, mirroring the auto-expire
      // setTimeout block in admit() lines 144-169 (the exact existing pattern).
      for (const did of droppedIdsRef.current) {
        refs.current.delete(did);
      }
      if (droppedIdsRef.current.length > 0) {
        setPreemptedIds((s) => {
          let next = s;
          for (const did of droppedIdsRef.current) {
            if (next.has(did)) {
              if (next === s) next = new Set(s);
              next.delete(did);
            }
          }
          return next;
        });
        setTailDirs((m) => {
          let changed = false;
          const next: typeof m = { ...m };
          for (const did of droppedIdsRef.current) {
            if (did in next) {
              delete next[did];
              changed = true;
            }
          }
          return changed ? next : m;
        });
      }
    });

    return (): void => {
      offSimTick();
      offSpoke();
      offAdult();
      offFox();
      offFear();
      offShout();
      offCatch();
      offFreeAll();
      offDog();
      offHidingEntered();
    };
    // intentionally empty deps — subscribe once at mount.
  }, []);

  // rAF position update loop (Pitfall 11 — write CSS custom properties
  // directly via node.style.setProperty so React does NOT re-render at 60Hz).
  useEffect(() => {
    let raf = 0;
    function tick(): void {
      const scene = getGameSceneRef();
      if (scene) {
        for (const b of bubbles) {
          const pos = scene.getSpriteScreenPos(b.entityId);
          const node = refs.current.get(b.id)?.current;
          if (!pos || !node) continue;
          node.style.setProperty('--bx', `${pos.x - node.offsetWidth / 2}px`);
          node.style.setProperty('--by', `${pos.y - 40}px`);
        }
      }
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return (): void => cancelAnimationFrame(raf);
  }, [bubbles]);

  // Tail auto-flip pass. Runs after bubbles change so newly mounted nodes
  // can read their getBoundingClientRect once and decide tail direction.
  // P6-Pitfall H: 8px hysteresis baked into decideTailDirection.
  useEffect(() => {
    let frame: number | null = null;
    frame = requestAnimationFrame(() => {
      const updates: Record<string, BubbleTailDirection> = {};
      let changed = false;
      for (const b of bubbles) {
        const node = refs.current.get(b.id)?.current;
        if (!node) continue;
        const dir = decideTailDirection(node);
        if (tailDirs[b.id] !== dir) {
          updates[b.id] = dir;
          changed = true;
        }
      }
      if (changed) setTailDirs((prev) => ({ ...prev, ...updates }));
    });
    return (): void => {
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [bubbles, tailDirs]);

  return (
    <div className="bubble-layer" data-testid="bubble-layer">
      {bubbles.map((b) => {
        let ref = refs.current.get(b.id);
        if (!ref) {
          ref = createRef<HTMLDivElement>();
          refs.current.set(b.id, ref);
        }
        const tier: BubbleTier = b.tier;
        const tail = tailDirs[b.id] ?? 'down';
        const preempted = preemptedIds.has(b.id);
        return (
          <Bubble
            key={b.id}
            text={b.text}
            tier={tier}
            foxguard={b.foxguard}
            reducedMotion={reducedMotion}
            bubbleRef={ref}
            preempted={preempted}
            tailDirection={tail}
          />
        );
      })}
    </div>
  );
}

// Compile-time witness: TIER_RANK is referenced so consumers/tests cannot
// remove the import accidentally during refactors. Not exported — purely
// load-bearing for grep audits ("BubbleLayer subscribes to tier ranks").
void TIER_RANK;
